import type { WeekDay } from "@/lib/calendarTypes";

/** Shared calendar event shape — extracted verbatim from client/src/pages/Calendar.tsx. */
export interface CalEvent {
  id: string;
  subject: string | null;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  isAllDay: boolean;
  location: { displayName: string } | null;
  organizer: { emailAddress: { name: string; address: string } } | null;
  bodyPreview: string | null;
  webLink: string | null;
  showAs: string | null;
  importance: string | null;
  /** "singleInstance" | "occurrence" | "exception" | "seriesMaster" */
  type?: string | null;
  seriesMasterId?: string | null;
  /** True when the viewing user organized this event on their own calendar — the edit/delete gate. */
  isOrganizer?: boolean | null;
}

/**
 * `iso` is an Eastern-local wall-clock string with no `Z` (Graph's
 * `Prefer: outlook.timezone="Eastern Standard Time"` header) — e.g.
 * "2026-09-02T14:30:00". `new Date(iso)` parses that in the BROWSER's
 * timezone, then reformatting it with `timeZone: "America/New_York"`
 * shifts it a SECOND time for any viewer not already on Eastern — a
 * double conversion. Read the hour/minute directly out of the string
 * instead (same technique as client/src/lib/calendarLayout.ts), which is
 * correct regardless of the viewer's timezone.
 */
export function formatTime(iso: string): string {
  const hh = Number(iso.slice(11, 13));
  const mm = Number(iso.slice(14, 16));
  if (Number.isNaN(hh) || Number.isNaN(mm)) {
    try {
      return new Date(iso).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/New_York",
      });
    } catch {
      return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    }
  }
  const period = hh >= 12 ? "PM" : "AM";
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${hour12}:${mm < 10 ? `0${mm}` : mm} ${period}`;
}

export function showAsDot(showAs: string | null): string {
  switch (showAs) {
    case "busy":
      return "bg-blue-500";
    case "tentative":
      return "bg-amber-400";
    case "oof":
      return "bg-red-500";
    case "workingElsewhere":
      return "bg-purple-400";
    default:
      return "bg-slate-400"; // "free" or unknown
  }
}

export const SHOW_AS_OPTIONS = [
  { value: "busy", label: "Busy" },
  { value: "free", label: "Free" },
  { value: "tentative", label: "Tentative" },
  { value: "oof", label: "Out of Office" },
  { value: "workingElsewhere", label: "Working Elsewhere" },
] as const;

export const RECURRENCE_OPTIONS = [
  { value: "none", label: "Doesn't repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
] as const;
export type RecurrenceUiPattern = (typeof RECURRENCE_OPTIONS)[number]["value"];

export const WEEKDAY_OPTIONS: Array<{ value: WeekDay; label: string }> = [
  { value: "sunday", label: "Sun" },
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
];

export function toDateInput(iso: string): string {
  return iso.slice(0, 10);
}
export function toTimeInput(iso: string): string {
  // event.start.dateTime from Graph calendarView (Prefer: outlook.timezone=Eastern)
  // is already local-ET wall time with no offset — safe to slice directly.
  return iso.slice(11, 16);
}
